import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';

interface PasskeyContextType {
  passkey: string | null;
  isUnlocked: boolean;
  setPasskey: (passkey: string) => void;
  clearPasskey: () => void;
  checkingPasskey: boolean;
}

const PasskeyContext = createContext<PasskeyContextType | undefined>(undefined);

export const usePasskey = (): PasskeyContextType => {
  const context = useContext(PasskeyContext);
  if (!context) {
    throw new Error('usePasskey must be used within a PasskeyProvider');
  }
  return context;
};

interface PasskeyProviderProps {
  children: ReactNode;
}

const PASSKEY_STORAGE_KEY = 'mythoughts_passkey';

export const PasskeyProvider: React.FC<PasskeyProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const [passkey, setPasskeyState] = useState<string | null>(null);
  const [checkingPasskey, setCheckingPasskey] = useState<boolean>(true);

  useEffect(() => {
    if (currentUser) {
      // Try to load passkey from localStorage
      const storedPasskey = localStorage.getItem(PASSKEY_STORAGE_KEY);
      console.log('🔍 Loading passkey from localStorage...');
      if (storedPasskey) {
        console.log('✅ Found passkey, length:', storedPasskey.length);
        setPasskeyState(storedPasskey);
      } else {
        console.log('❌ No passkey found in localStorage');
      }
      setCheckingPasskey(false);
    } else {
      // Clear passkey when user logs out
      setPasskeyState(null);
      localStorage.removeItem(PASSKEY_STORAGE_KEY);
      setCheckingPasskey(false);
    }
  }, [currentUser]);

  const setPasskey = (newPasskey: string) => {
    console.log('🔑 Setting new passkey, length:', newPasskey.length);
    setPasskeyState(newPasskey);
    localStorage.setItem(PASSKEY_STORAGE_KEY, newPasskey);
    console.log('✅ Passkey saved to localStorage');
  };

  const clearPasskey = () => {
    setPasskeyState(null);
    localStorage.removeItem(PASSKEY_STORAGE_KEY);
  };

  const value: PasskeyContextType = {
    passkey,
    isUnlocked: !!passkey,
    setPasskey,
    clearPasskey,
    checkingPasskey,
  };

  return <PasskeyContext.Provider value={value}>{children}</PasskeyContext.Provider>;
};

